import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../../../layouts/MainLayout';
import { Button, Card, Descriptions, Tag, Avatar, Space, message, Progress, Popconfirm } from 'antd';
import {
  EditOutlined,
  ArrowRightOutlined,
  UserOutlined,
  CalendarOutlined,
  CheckOutlined,
  CloseOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { leavesApi, workflowApi } from '../../../utils/api';
import { unwrapLeaveRecord, formatLeaveDurationLabel } from '../../../utils/leaveDurationUtils';
import { DetailPageSkeleton } from '../../../components/SkeletonLoaders';
import WorkflowTimeline from '../../../components/workflow/WorkflowTimeline';
import WorkflowActionModal from '../../../components/workflow/WorkflowActionModal';
import moment from 'moment-jalaali';
import { useDetailReturnRefs } from '../../../hooks/useListReturnState';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useAuth } from '../../../contexts/AuthContext';

const statusMap = {
  pending: { label: 'در انتظار', color: 'orange' },
  approved: { label: 'تایید شده', color: 'green' },
  rejected: { label: 'رد شده', color: 'red' },
};

const ViewLeave = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user: authUser } = useAuth();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const { canEdit } = useResourcePermissions('leaves');
  const goBackToList = () =>
    navigate(returnToRef.current || '/hr/leaves', { state: buildGoBackState() });
  const [loading, setLoading] = useState(true);
  const [leave, setLeave] = useState(null);
  const [actionOpen, setActionOpen] = useState(false);
  const [actionMode, setActionMode] = useState('approve');
  const [cancelLoading, setCancelLoading] = useState(false);

  const fetchLeave = useCallback(async () => {
    setLoading(true);
    try {
      const res = await leavesApi.getById(id);
      setLeave(unwrapLeaveRecord(res));
    } catch (error) {
      console.error('خطا در دریافت اطلاعات مرخصی:', error);
      message.error('خطا در دریافت اطلاعات مرخصی');
      goBackToList();
    } finally {
      setLoading(false);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchLeave();
  }, [fetchLeave]);

  const formatUserRef = (ref) => {
    if (ref == null || ref === '') return '--';
    if (typeof ref === 'object') {
      return ref.name_salutation || ref.name || ref.email || (ref.id != null ? String(ref.id) : '--');
    }
    return String(ref);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    try {
      const m = moment(dateStr);
      if (m.isValid()) {
        return m.format('jYYYY/jMM/jDD');
      }
    } catch (e) {}
    return dateStr;
  };

  const workflow = leave?.workflow || null;
  const instanceId = workflow?.instance_id || workflow?.id || null;
  const canApprove = Boolean(workflow?.can_be_approved_by_me);
  const isRequester =
    authUser?.id != null &&
    leave?.user_id != null &&
    Number(authUser.id) === Number(leave.user_id);
  const canCancelWorkflow =
    isRequester &&
    instanceId != null &&
    ['pending', 'in_progress'].includes(String(workflow?.status || ''));

  const handleCancelWorkflow = async () => {
    if (instanceId == null) return;
    setCancelLoading(true);
    try {
      await workflowApi.cancel(instanceId);
      message.success('درخواست با موفقیت لغو شد');
      fetchLeave();
    } catch (error) {
      message.error(error?.message || 'خطا در لغو درخواست');
    } finally {
      setCancelLoading(false);
    }
  };

  if (loading || !leave) {
    return (
      <MainLayout>
        <div className="" style={{ fontFamily: 'CustomFont' }}>
          <DetailPageSkeleton />
        </div>
      </MainLayout>
    );
  }

  const user = leave.user || {};
  const statusInfo = statusMap[leave.status] || { label: leave.status, color: 'default' };

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                <CalendarOutlined className="text-blue-600 dark:text-blue-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">جزئیات مرخصی</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">شناسه: {leave.unique_id || `#${leave.id}`}</p>
              </div>
            </div>
            <Space wrap>
              <Button icon={<ArrowRightOutlined />} onClick={goBackToList} style={{ fontFamily: 'CustomFont' }}>
                بازگشت
              </Button>
              {canApprove ? (
                <>
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    className="bg-emerald-600 hover:bg-emerald-700 border-emerald-600"
                    style={{ fontFamily: 'CustomFont' }}
                    onClick={() => {
                      setActionMode('approve');
                      setActionOpen(true);
                    }}
                  >
                    تأیید مرحله
                  </Button>
                  <Button
                    danger
                    icon={<CloseOutlined />}
                    style={{ fontFamily: 'CustomFont' }}
                    onClick={() => {
                      setActionMode('reject');
                      setActionOpen(true);
                    }}
                  >
                    رد
                  </Button>
                </>
              ) : null}
              {canCancelWorkflow ? (
                <Popconfirm
                  title="لغو درخواست مرخصی؟"
                  description="فرآیند تأیید متوقف و درخواست لغو می‌شود."
                  okText="بله، لغو کن"
                  cancelText="انصراف"
                  onConfirm={handleCancelWorkflow}
                >
                  <Button
                    icon={<StopOutlined />}
                    loading={cancelLoading}
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    لغو درخواست
                  </Button>
                </Popconfirm>
              ) : null}
              {canEdit ? (
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() =>
                    navigate(`/hr/leaves/${id}/edit`, {
                      state: { returnTo: returnToRef.current, ...buildGoBackState() },
                    })
                  }
                  className="bg-blue-600 hover:bg-blue-700 border-0"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  ویرایش
                </Button>
              ) : null}
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <Card className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 mb-6">
              <div className="flex items-center gap-4 mb-2">
                <Avatar
                  size={64}
                  src={user.image_url}
                  icon={<UserOutlined />}
                  style={{ backgroundColor: '#1890ff' }}
                >
                  {user.name ? user.name.charAt(0) : ''}
                </Avatar>
                <div>
                  <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200 m-0" style={{ fontFamily: 'CustomFont' }}>
                    {user.name_salutation || user.name || '--'}
                  </h2>
                  <p className="text-gray-500 dark:text-gray-400 text-sm m-0" style={{ fontFamily: 'CustomFont' }}>
                    {user.email || '--'}
                  </p>
                  <Space className="mt-2" wrap>
                    <Tag color={statusInfo.color} style={{ fontFamily: 'CustomFont' }}>
                      {statusInfo.label}
                    </Tag>
                    {workflow?.status_label ? (
                      <Tag color="processing" style={{ fontFamily: 'CustomFont' }}>
                        فرآیند: {workflow.status_label}
                      </Tag>
                    ) : null}
                  </Space>
                </div>
              </div>
              {workflow && Number.isFinite(Number(workflow.progress_percentage)) ? (
                <div className="mt-3 max-w-md">
                  <Progress
                    percent={Math.round(Number(workflow.progress_percentage))}
                    size="small"
                    status={
                      workflow.status === 'rejected'
                        ? 'exception'
                        : workflow.status === 'approved' || workflow.status === 'auto_approved'
                          ? 'success'
                          : 'active'
                    }
                  />
                </div>
              ) : null}
            </Card>

            {workflow ? (
              <Card
                title={<span style={{ fontFamily: 'CustomFont' }}>زنجیره تأیید (Workflow)</span>}
                className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 mb-6"
              >
                <WorkflowTimeline workflow={workflow} />
              </Card>
            ) : null}

            <Card
              title={<span style={{ fontFamily: 'CustomFont' }}>اطلاعات مرخصی</span>}
              className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800"
            >
              <Descriptions
                bordered
                column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
                style={{ fontFamily: 'CustomFont' }}
              >
                <Descriptions.Item label="نام کارمند">
                  {user.name_salutation || user.name || '--'}
                </Descriptions.Item>
                <Descriptions.Item label="ایمیل">
                  {user.email || '--'}
                </Descriptions.Item>
                <Descriptions.Item label="تاریخ مرخصی">
                  {leave.duration === 'multiple'
                    ? `${formatDate(leave.multi_start_date || leave.multiStartDate)} تا ${formatDate(leave.multi_end_date || leave.multiEndDate)}`
                    : formatDate(leave.leave_date)}
                </Descriptions.Item>
                <Descriptions.Item label="مدت زمان">
                  {formatLeaveDurationLabel(leave.duration, leave.half_day_type)}
                </Descriptions.Item>
                {(leave.duration === 'hourly' || leave.start_time) && (
                  <Descriptions.Item label="از ساعت">
                    {leave.start_time ? String(leave.start_time).slice(0, 5) : '--'}
                  </Descriptions.Item>
                )}
                {(leave.duration === 'hourly' || leave.end_time) && (
                  <Descriptions.Item label="تا ساعت">
                    {leave.end_time ? String(leave.end_time).slice(0, 5) : '--'}
                  </Descriptions.Item>
                )}
                {leave.total_hours != null && (
                  <Descriptions.Item label="مجموع ساعات">
                    {leave.total_hours}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="وضعیت">
                  <Tag color={statusInfo.color} style={{ fontFamily: 'CustomFont' }}>
                    {statusInfo.label}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="وضعیت پرداخت">
                  {leave.paid === 1 ? (
                    <Tag color="green" style={{ fontFamily: 'CustomFont' }}>با حقوق</Tag>
                  ) : (
                    <Tag color="red" style={{ fontFamily: 'CustomFont' }}>بدون حقوق</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="استفاده بیش از حد">
                  {leave.over_utilized === 1 ? 'بله' : 'خیر'}
                </Descriptions.Item>
                <Descriptions.Item label="دلیل مرخصی" span={2}>
                  {leave.reason || '--'}
                </Descriptions.Item>
                {leave.reject_reason && (
                  <Descriptions.Item label="دلیل رد" span={2}>
                    <span className="text-red-500">{leave.reject_reason}</span>
                  </Descriptions.Item>
                )}
                {leave.approve_reason && (
                  <Descriptions.Item label="دلیل تایید" span={2}>
                    <span className="text-green-500">{leave.approve_reason}</span>
                  </Descriptions.Item>
                )}
                {leave.approved_by && (
                  <Descriptions.Item label="تایید شده توسط">
                    {formatUserRef(leave.approved_by)}
                  </Descriptions.Item>
                )}
                {leave.approved_at && (
                  <Descriptions.Item label="تاریخ تایید">
                    {formatDate(leave.approved_at)}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          </div>
        </Card>

        <WorkflowActionModal
          open={actionOpen}
          mode={actionMode}
          instanceId={instanceId}
          summaryTitle={`مرخصی ${user.name || ''}`.trim()}
          requester={user}
          moduleType="leave"
          stepName={
            Array.isArray(workflow?.steps)
              ? workflow.steps.find((s) => s.status === 'pending')?.name
              : null
          }
          onClose={() => setActionOpen(false)}
          onSuccess={fetchLeave}
        />
      </div>
    </MainLayout>
  );
};

export default ViewLeave;
